import { useEffect, useState } from "react";
import { Feature, Point } from "geojson";

// Project import
import { FirestoreInventoryItemProps } from "types/auth";
import { getInventoryDisplayReference } from "utils/inventoryDisplayReference";
import useMapData from "./useMapData";
import { useTranslation } from "utils/locales/utilityFunctions";
import useSpeciesData from "./useSpeciesData";

const useInventoryData = () => {
  // --- Hooks --- //
  const { inventories, inventoryMeasurements, changeInventory, removeInventoryId } = useMapData();
  const { getPointIconStrFromId } = useSpeciesData();
  const { translate } = useTranslation();

  // --- State and references --- //
  const [inventoryFeatures, setInventoryFeatures] = useState<{ [key: string]: Feature<Point> }>({});

  // --- Effects --- //
  useEffect(() => {
    if (inventories === null || Object.keys(inventories).length === 0) {
      setInventoryFeatures({});
      return;
    }
    // Convert inventory items to geojson features
    const newInventoryData: { [key: string]: Feature<Point> } = {};
    Object.keys(inventories).forEach((inventoryId) => {
      const inventoryItem = inventories[inventoryId];
      if (inventoryItem) {
        newInventoryData[inventoryId] = convertInventoryItemToGeoJsonFeature({
          inventoryItem,
          getPointIconStrFromId,
          translate,
        });
      }
    });
    setInventoryFeatures(newInventoryData);
  }, [inventories, translate("render-change")]);

  // --- Functions --- //
  const getInventoryFeaturesByIdAndType = (
    id: string,
    type: "estate" | "map" | "project" | "activity"
  ): Feature<Point>[] => {
    if (!inventoryFeatures || Object.keys(inventoryFeatures).length === 0) {
      return [];
    }
    const inventoryItems = Object.values(inventoryFeatures).filter((item) => {
      const { properties } = item;
      const { origins } = properties as any;
      if (!origins) return false;
      const { estateId, mapId, projectId, activityId } = origins;
      if (type === "estate" && estateId === id) return true;
      if (type === "map" && mapId === id) return true;
      if (type === "project" && projectId === id) return true;
      if (type === "activity" && activityId === id) return true;
      return false;
    });
    return inventoryItems;
  };

  const getInventoryFeatureById = (id: string): Feature<Point> | null => {
    if (!inventoryFeatures || Object.keys(inventoryFeatures).length === 0) {
      return null;
    }
    return inventoryFeatures[id] || null;
  };

  const getInventoryItemById = (
    id: string
  ): FirestoreInventoryItemProps | null => {
    if (!inventories || Object.keys(inventories).length === 0) {
      return null;
    }
    return inventories[id] || null;
  };

  const getParametersFromInventoryType = (
    inventoryType: FirestoreInventoryItemProps["inventoryType"]
  ) => {
    let out = {
      title: translate("input-dialog-raw-wood-wood-stack"),
    };
    if (inventoryType === "WoodLogStack") {
      out.title = translate("input-dialog-raw-wood-wood-log-stack");
    } else if (inventoryType === "WoodChipStack") {
      out.title = translate("input-dialog-raw-wood-wood-chip-stack");
    }
    return out;
  };

  return {
    inventories,
    inventoryMeasurements,
    inventoryFeatures,
    changeInventory,
    removeInventoryId,
    getInventoryFeaturesByIdAndType,
    getInventoryFeatureById,
    getInventoryItemById,
    getParametersFromInventoryType,
  };
};

export default useInventoryData;

// --- Auxiliary functions --- //
type ConvertInventoryItemToGeoJsonFeatureProps = {
  inventoryItem: FirestoreInventoryItemProps;
  getPointIconStrFromId: (id: string) => string;
  translate: (key: string) => string;
};
function convertInventoryItemToGeoJsonFeature({
  inventoryItem,
  getPointIconStrFromId,
  translate,
}: ConvertInventoryItemToGeoJsonFeatureProps): Feature<Point> {
  const { coordinates, ...rest } = inventoryItem;
  const invType = inventoryItem.inventoryType;
  const styleObject = {
    pointIcon: getPointIconStrFromId(invType),
  };
  return {
    type: "Feature",
    geometry: {
      type: "Point",
      coordinates: [coordinates.lng, coordinates.lat], // Assuming inventoryItem has longitude and latitude properties
    },
    properties: {
      ...rest,
      type: "Point",
      title:
        translate(`inventory-item-${invType}`) + " " + getInventoryDisplayReference(inventoryItem),
      styleObject,
    },
  };
}
